import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, input, OnDestroy, OnInit, output, signal, viewChild, viewChildren } from '@angular/core';

import { LayoutType } from 'src/app/model/enums/layout-type.enum';
import { SlideBuilderSection } from 'src/app/model/slide-builder-section';
import { SlideDTO } from 'src/app/model/slideDTO.model';
import { GeneralWidgetComponent } from '../general-widget/general-widget.component';
import { QuestionWidgetComponent } from '../question-widget/question-widget.component';
import { BackgroundSizeOption, BaseSlideBuilderSectionGridLayoutComponent, SlideBuilderSectionLayoutType } from 'library-explorer';
import { SlideBuilderSectionBackgroundComponent } from '../slide-builder-section-background/slide-builder-section-background.component';

@Component({
    selector: 'app-slide-builder-section',
    templateUrl: './slide-builder-section.component.html',
    styleUrls: ['./slide-builder-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SlideBuilderSectionComponent extends BaseSlideBuilderSectionGridLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  public slideBackgroundComponent = viewChild(SlideBuilderSectionBackgroundComponent);

  public baseSlides = viewChildren(GeneralWidgetComponent);
  public questionBaseSlides = viewChildren(QuestionWidgetComponent);
  public leftColumn = viewChild<ElementRef>('leftColumn');
  public rightColumn = viewChild<ElementRef>('rightColumn');

  public section = input<SlideBuilderSection>();
  public leftPosition = input(0);
  public parentContainerHeight = input(0);
  public chapterContainerHeight = input<number>();
  public requiredToWatchVideo = input(false);
  public requiredToCompleteInteraction = input(false);
  public isFirstSection = input(false);
  public isLastSection = input(false);

  public answerChanged = output<{ slide: SlideDTO, answer: any }>();
  public interactionCompleted = output<{ slide: SlideDTO, completionRatio: number }>();
  public videoWatched = output<SlideDTO>();

  public readonly gridLayoutConfig = computed(() => this.section().layoutConfig);
  protected readonly widgetHeights = signal<Record<string, number>>({});
  protected readonly gridWidgets = computed(() => this.section()?.items ?? []);
  
  public readonly layoutTypes: typeof LayoutType = LayoutType;
  public readonly sectionLayoutTypes: typeof SlideBuilderSectionLayoutType = SlideBuilderSectionLayoutType;
  public answer: any;

  public aspectRatio?: string;

  private readonly defaultColumnWidth = 50;

  private readonly DEFAULT_SPACING = 32;

  constructor(public readonly elementRef: ElementRef) {
    super(elementRef);
  }

  ngOnInit() {
    this.setSlideColumnsWidth();
    this.setSlideColumnPaddings();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public backgroundMediaRatioChanged(ratio: string): void {
    this.aspectRatio = this.section()?.backgroundSize === BackgroundSizeOption.FULL ? ratio : '';
  }

  public slideAnswerChanged(slide: SlideDTO, answer: any): void {
    this.answerChanged.emit( { slide, answer });
  }

  public emitVideoWatched(data: SlideDTO): void {
    this.videoWatched.emit(data);
  }

  public slideInteractionCompleted(data: { slide: SlideDTO, completionRatio: number }): void {
    this.interactionCompleted.emit(data);
  }

  protected setWidgetHeight(widgetId: string, height: number): void {
    this.widgetHeights.update(widgetHeights => {
      if (widgetHeights[widgetId] === height) {
        return widgetHeights;
      }

      return { ...widgetHeights, [widgetId]: height };
    });
  }

  private setSlideColumnsWidth(): void {
    const ratio = this.section()?.columnsRatio || 1;

    const columnWidth = this.defaultColumnWidth * ratio;
    this.elementRef.nativeElement.style.setProperty(`--lms-slide-left-column-width`, `${columnWidth}%`);
    this.elementRef.nativeElement.style.setProperty(`--lms-slide-right-column-width`, `${100 - columnWidth}%`);
  }

  private setSlideColumnPaddings(): void {
    const section = this.section();

    if (!section?.spacing) {
      return;
    }
  
    const { spacing } = section;

    const sides = ['top', 'right', 'bottom', 'left'];

    sides.forEach(side => {
      const value = spacing?.[side] ?? this.DEFAULT_SPACING;
      this.elementRef.nativeElement.style.setProperty(`--lms-slide-row-padding-${side}`, `${value}px`);
    });
  }
}
